import type {
  AlertEvent,
  AlertPolicy,
  AssetClass,
  AssetRecord,
  CatalogInstrumentRecord,
  ChartIntelligencePayload,
  ChartSettings,
  DailyBar,
  ExecutionRecord,
  MonitoringMetric,
  MonitoringWindow,
  ProviderConfigRecord,
  ProviderHealthRecord,
  RefreshRunRecord,
  RulePlanBundle
} from "../types";
import { defaultChartSettings, mergeChartSettings } from "./chartSettings";
import { alertEventsMock, executionRecords, monitoringMetricsMock, planBundlesMock } from "./mockData";

const defaultApiBase = "https://cp-dashboard-api.jirehkwon.workers.dev";
const apiBase = (import.meta.env.VITE_API_BASE_URL ?? defaultApiBase).trim();
const adminToken = import.meta.env.VITE_ADMIN_API_TOKEN ?? "";

export interface DataStatusPayload {
  latestUpdate: string | null;
  latestMarketAsOf?: string | null;
  oldestMarketAsOf?: string | null;
  freshnessCounts?: Record<string, number>;
  instrumentStatus?: Array<{ instrumentId: string; source: string | null; asOf: string | null; updatedAt: string | null; freshness: string; staleReason?: string }>;
  checkedAt: string;
}

function authHeaders(): Record<string, string> {
  return adminToken ? { authorization: `Bearer ${adminToken}` } : {};
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = 20000) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) throw new Error("Server request timed out. Please retry.");
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function responseError(response: Response, prefix: string) {
  const payload = await response.json().catch(() => null) as { error?: string } | null;
  return new Error(payload?.error ? `${prefix}: ${payload.error}` : `${prefix}: HTTP ${response.status}`);
}

const storage = {
  get<T>(key: string, fallback: T): T {
    try {
      const value = localStorage.getItem(key);
      return value ? (JSON.parse(value) as T) : fallback;
    } catch {
      return fallback;
    }
  },
  set<T>(key: string, value: T) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Browser storage is optional; API-backed sessions do not need this.
    }
  }
};

const defaultAssets: AssetRecord[] = [
  { ticker: "SPY", name: "SPDR S&P 500 ETF", assetClass: "ETF", market: "US", currency: "USD", defaultWeightPct: 30, active: true },
  { ticker: "QQQ", name: "Invesco QQQ Trust", assetClass: "ETF", market: "US", currency: "USD", defaultWeightPct: 20, active: true },
  { ticker: "KODEX 200", name: "KODEX 200 ETF", assetClass: "ETF", market: "KR", currency: "KRW", defaultWeightPct: 20, active: true },
  { ticker: "Cash", name: "USD Reserve", assetClass: "Cash", market: "Global", currency: "USD", defaultWeightPct: 10, active: true },
  { ticker: "Others", name: "Tactical Sleeve", assetClass: "Mixed", market: "Global", currency: "USD", defaultWeightPct: 20, active: true }
];

type MonitoringSummaryOptions = { window?: MonitoringWindow; assetClass?: AssetClass; group?: "overview" | "all" };
type MonitoringRequestOptions = { force?: boolean };
type TimedCacheEntry<T> = { value?: T; expiresAt: number; pending?: Promise<T> };

const monitoringCacheTtlMs = 5 * 60 * 1000;
const monitoringSummaryCache = new Map<string, TimedCacheEntry<MonitoringMetric[]>>();
const monitoringHistoryCache = new Map<string, TimedCacheEntry<DailyBar[]>>();
let monitoringCacheGeneration = 0;

function monitoringSummaryKey(options: MonitoringSummaryOptions) {
  return `${options.window ?? "5Y"}|${options.group ?? "overview"}|${options.assetClass ?? "all"}`;
}

function monitoringHistoryKey(instrumentId: string, range: MonitoringWindow) {
  return `${instrumentId}|${range}`;
}

function freshCacheValue<T>(entry: TimedCacheEntry<T> | undefined) {
  return entry?.value !== undefined && entry.expiresAt > Date.now() ? entry.value : undefined;
}

function storeCacheEntry<T>(cache: Map<string, TimedCacheEntry<T>>, key: string, entry: TimedCacheEntry<T>, maxEntries: number) {
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > maxEntries) {
    const oldestKey = cache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    cache.delete(oldestKey);
  }
}

export function peekMonitoringSummary(options: MonitoringSummaryOptions = {}) {
  return freshCacheValue(monitoringSummaryCache.get(monitoringSummaryKey(options)));
}

export function peekMonitoringHistory(instrumentId: string, range: MonitoringWindow = "5Y") {
  return freshCacheValue(monitoringHistoryCache.get(monitoringHistoryKey(instrumentId, range)));
}

export function clearMonitoringClientCache() {
  monitoringCacheGeneration += 1;
  monitoringSummaryCache.clear();
  monitoringHistoryCache.clear();
}

function nonEmptyPlans(plans: RulePlanBundle[]) {
  return plans.length ? plans : planBundlesMock;
}

function nonEmptyAssets(assets: AssetRecord[]) {
  return assets.length ? assets : defaultAssets;
}

export async function getMonitoringSummary(options: MonitoringSummaryOptions = {}, requestOptions: MonitoringRequestOptions = {}): Promise<MonitoringMetric[]> {
  const window = options.window ?? "5Y";
  const group = options.group ?? "overview";
  if (!apiBase) {
    return monitoringMetricsMock
      .filter((metric) => !options.assetClass || metric.instrument.assetClass === options.assetClass)
      .map((metric) => ({ ...metric, window }));
  }

  const key = monitoringSummaryKey(options);
  const current = monitoringSummaryCache.get(key);
  const generation = monitoringCacheGeneration;
  const cached = freshCacheValue(current);
  if (!requestOptions.force && cached) return cached;
  if (!requestOptions.force && current?.pending) return current.pending;

  const pending = (async () => {
    const query = new URLSearchParams({ window, group });
    if (options.assetClass) query.set("assetClass", options.assetClass);
    const response = await fetchWithTimeout(
      `${apiBase}/api/monitoring/summary?${query.toString()}`,
      { cache: requestOptions.force ? "reload" : "default" },
      12000
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as { metrics: MonitoringMetric[] };
    return payload.metrics;
  })();
  storeCacheEntry(monitoringSummaryCache, key, { value: current?.value, expiresAt: current?.expiresAt ?? 0, pending }, 16);

  try {
    const metrics = await pending;
    if (generation === monitoringCacheGeneration) {
      storeCacheEntry(monitoringSummaryCache, key, { value: metrics, expiresAt: Date.now() + monitoringCacheTtlMs }, 16);
    }
    return metrics;
  } catch {
    if (current?.value) {
      if (generation === monitoringCacheGeneration) storeCacheEntry(monitoringSummaryCache, key, current, 16);
      return current.value;
    }
    monitoringSummaryCache.delete(key);
    return [];
  }
}

export async function getMonitoringHistory(instrumentId: string, range: MonitoringWindow = "5Y", requestOptions: MonitoringRequestOptions = {}): Promise<DailyBar[]> {
  if (!apiBase) {
    return monitoringMetricsMock.find((metric) => metric.instrument.id === instrumentId)?.history ?? [];
  }

  const key = monitoringHistoryKey(instrumentId, range);
  const current = monitoringHistoryCache.get(key);
  const generation = monitoringCacheGeneration;
  const cached = freshCacheValue(current);
  if (!requestOptions.force && cached) return cached;
  if (!requestOptions.force && current?.pending) return current.pending;

  const pending = (async () => {
    const response = await fetchWithTimeout(
      `${apiBase}/api/monitoring/instruments/${encodeURIComponent(instrumentId)}/history?range=${encodeURIComponent(range)}`,
      { cache: requestOptions.force ? "reload" : "default" },
      20000
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as { history: DailyBar[] };
    return payload.history;
  })();
  storeCacheEntry(monitoringHistoryCache, key, { value: current?.value, expiresAt: current?.expiresAt ?? 0, pending }, 40);

  try {
    const history = await pending;
    if (generation === monitoringCacheGeneration) {
      storeCacheEntry(monitoringHistoryCache, key, { value: history, expiresAt: Date.now() + monitoringCacheTtlMs }, 40);
    }
    return history;
  } catch {
    if (current?.value) {
      if (generation === monitoringCacheGeneration) storeCacheEntry(monitoringHistoryCache, key, current, 40);
      return current.value;
    }
    monitoringHistoryCache.delete(key);
    return [];
  }
}

export async function getCatalogInstruments(filters: { assetClass?: AssetClass; status?: CatalogInstrumentRecord["catalogStatus"] } = {}): Promise<CatalogInstrumentRecord[]> {
  if (!apiBase) return [];
  const query = new URLSearchParams();
  if (filters.assetClass) query.set("assetClass", filters.assetClass);
  if (filters.status) query.set("status", filters.status);
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await fetchWithTimeout(`${apiBase}/api/catalog/instruments${suffix}`, { cache: "no-store" }, 12000);
  if (!response.ok) throw new Error(`Catalog failed: ${response.status}`);
  const payload = (await response.json()) as { instruments: CatalogInstrumentRecord[] };
  return payload.instruments;
}

export async function refreshMonitoring(instrumentId?: string) {
  if (!apiBase) return { ok: true, enqueued: false, run: undefined as RefreshRunRecord | undefined };
  const response = await fetchWithTimeout(`${apiBase}/api/admin/refresh`, {
    method: "POST",
    cache: "no-store",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify({ instrumentId })
  });
  if (!response.ok) throw new Error(`Refresh failed: ${response.status}`);
  return response.json() as Promise<{ ok: boolean; enqueued: boolean; run?: RefreshRunRecord }>;
}

export async function getRefreshRun(runId: string): Promise<RefreshRunRecord> {
  const response = await fetchWithTimeout(`${apiBase}/api/admin/refresh/${encodeURIComponent(runId)}`, {
    cache: "no-store",
    headers: authHeaders()
  }, 12000);
  if (!response.ok) throw await responseError(response, "Refresh status failed");
  const payload = (await response.json()) as { run: RefreshRunRecord };
  return payload.run;
}

export async function getPlans(): Promise<RulePlanBundle[]> {
  if (!apiBase) return nonEmptyPlans(storage.get("cp-plans", planBundlesMock));
  try {
    const response = await fetchWithTimeout(`${apiBase}/api/plans`, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as { plans: RulePlanBundle[] };
    return payload.plans;
  } catch {
    return [];
  }
}

export async function savePlan(plan: RulePlanBundle, mode: "auto" | "manual" = "manual"): Promise<RulePlanBundle> {
  if (!apiBase) {
    const plans = storage.get("cp-plans", planBundlesMock);
    const next = [plan, ...plans.filter((item) => item.id !== plan.id)];
    storage.set("cp-plans", next);
    return plan;
  }
  const response = await fetchWithTimeout(`${apiBase}/api/plans`, {
    method: "POST",
    headers: { "content-type": "application/json", "x-cp-save-mode": mode, ...authHeaders() },
    body: JSON.stringify(plan)
  });
  if (!response.ok) throw await responseError(response, "Plan save failed");
  const payload = (await response.json()) as { plan: RulePlanBundle };
  return payload.plan;
}

export async function deletePlan(planId: string): Promise<RulePlanBundle[]> {
  if (!apiBase) {
    const plans = storage.get("cp-plans", planBundlesMock).filter((item) => item.id !== planId);
    const next = plans.length ? plans : planBundlesMock;
    storage.set("cp-plans", next);
    return next;
  }
  const response = await fetch(`${apiBase}/api/plans/${encodeURIComponent(planId)}`, {
    method: "DELETE",
    headers: authHeaders()
  });
  if (!response.ok) throw new Error(`Plan delete failed: ${response.status}`);
  const payload = (await response.json()) as { plans: RulePlanBundle[] };
  return payload.plans;
}

export async function getExecutions(planId?: string): Promise<ExecutionRecord[]> {
  if (!apiBase) return storage.get("cp-executions", executionRecords);
  const suffix = planId ? `?planId=${encodeURIComponent(planId)}` : "";
  try {
    const response = await fetchWithTimeout(`${apiBase}/api/executions${suffix}`, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as { executions: ExecutionRecord[] };
    return payload.executions;
  } catch {
    return [];
  }
}

export async function saveExecution(record: ExecutionRecord): Promise<ExecutionRecord> {
  if (!apiBase) {
    const records = storage.get("cp-executions", executionRecords);
    const next = records.map((item) => (item.stepId === record.stepId ? record : item));
    storage.set("cp-executions", next);
    return record;
  }
  const response = await fetch(`${apiBase}/api/executions`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(record)
  });
  if (!response.ok) throw new Error(`Execution save failed: ${response.status}`);
  const payload = (await response.json()) as { execution: ExecutionRecord };
  return payload.execution;
}

export async function getAlerts(): Promise<AlertEvent[]> {
  if (!apiBase) return storage.get("cp-alerts", alertEventsMock);
  try {
    const response = await fetch(`${apiBase}/api/alerts`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as { alerts: AlertEvent[] };
    return payload.alerts;
  } catch {
    return [];
  }
}

export async function sendTestAlert(input: { planId?: string; stepId?: string; severity: string; dedupeKey: string; cooldownUntil?: string; message: string }) {
  if (!apiBase) {
    const alert: AlertEvent = {
      id: `local-alert-${Date.now()}`,
      planId: input.planId,
      stepId: input.stepId,
      severity: input.severity,
      dedupeKey: input.dedupeKey,
      cooldownUntil: input.cooldownUntil,
      status: "sent",
      telegramPreview: input.message,
      sentAt: new Date().toISOString(),
      createdAt: new Date().toISOString()
    };
    const next = [alert, ...storage.get("cp-alerts", alertEventsMock)];
    storage.set("cp-alerts", next);
    return { ok: true, sent: true, alerts: next };
  }
  const response = await fetch(`${apiBase}/api/alerts/test`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(input)
  });
  const payload = (await response.json()) as { ok: boolean; sent?: boolean; alerts?: AlertEvent[]; error?: string };
  if (!response.ok) throw new Error(payload.error ?? `Alert test failed: ${response.status}`);
  return payload;
}

export async function acknowledgeAlert(id: string): Promise<AlertEvent[]> {
  if (!apiBase) {
    const next = storage.get("cp-alerts", alertEventsMock).map((alert) =>
      alert.id === id ? { ...alert, status: "acknowledged" as const, acknowledgedAt: new Date().toISOString() } : alert
    );
    storage.set("cp-alerts", next);
    return next;
  }
  const response = await fetch(`${apiBase}/api/alerts/ack`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify({ id })
  });
  if (!response.ok) throw new Error(`Alert ack failed: ${response.status}`);
  const payload = (await response.json()) as { alerts: AlertEvent[] };
  return payload.alerts;
}

export async function getTelegramSettings(): Promise<{ configured: boolean; chatIdMasked: string | null }> {
  if (!apiBase) return { configured: false, chatIdMasked: null };
  const response = await fetchWithTimeout(`${apiBase}/api/settings/telegram`);
  if (!response.ok) throw await responseError(response, "Telegram status failed");
  return response.json();
}

export async function saveTelegramSettings(input: { botToken: string; chatId: string }): Promise<{ ok: boolean; configured: boolean; chatIdMasked: string }> {
  if (!apiBase) {
    return { ok: true, configured: true, chatIdMasked: "local preview" };
  }

  const response = await fetchWithTimeout(`${apiBase}/api/settings/telegram`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify({ botToken: input.botToken.trim(), chatId: input.chatId.trim() })
  });

  if (!response.ok) {
    throw await responseError(response, "Telegram settings failed");
  }

  return response.json();
}

export async function getChartIntelligence(instrumentId = "spx", range: ChartSettings["chart"]["range"] = "6M"): Promise<ChartIntelligencePayload> {
  if (!apiBase) return chartFallback(instrumentId);
  try {
    const response = await fetchWithTimeout(`${apiBase}/api/chart-intelligence/${encodeURIComponent(instrumentId)}?range=${encodeURIComponent(range)}`, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  } catch {
    return chartFallback(instrumentId);
  }
}

export async function getChartSettings(): Promise<ChartSettings> {
  const fallback = apiBase ? defaultChartSettings : mergeChartSettings(storage.get("cp-chart-settings", defaultChartSettings));
  if (!apiBase) return fallback;

  try {
    const response = await fetch(`${apiBase}/api/chart-settings`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = (await response.json()) as { settings: ChartSettings };
    return mergeChartSettings(payload.settings);
  } catch {
    return fallback;
  }
}

export async function saveChartSettings(settings: ChartSettings): Promise<ChartSettings> {
  const merged = mergeChartSettings(settings);

  if (!apiBase) {
    storage.set("cp-chart-settings", merged);
    return merged;
  }

  const response = await fetchWithTimeout(`${apiBase}/api/chart-settings`, {
    method: "PUT",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(merged)
  });
  if (!response.ok) throw await responseError(response, "Chart settings save failed");
  const payload = (await response.json()) as { settings: ChartSettings };
  const saved = mergeChartSettings(payload.settings);
  return saved;
}

export async function getProviderHealth(): Promise<{ providers: ProviderConfigRecord[]; health: ProviderHealthRecord[] }> {
  if (!apiBase) return { providers: [], health: [] };
  const response = await fetch(`${apiBase}/api/provider-health`);
  if (!response.ok) throw new Error(`Provider health failed: ${response.status}`);
  return response.json();
}

export async function getDataStatus(): Promise<DataStatusPayload> {
  if (!apiBase) return { latestUpdate: null, checkedAt: new Date().toISOString() };
  const response = await fetchWithTimeout(`${apiBase}/api/data-status`, { cache: "no-store" });
  if (!response.ok) throw new Error(`Data status failed: ${response.status}`);
  return response.json();
}

export async function getApiHealth(): Promise<{ ok: boolean; environment: string }> {
  if (!apiBase) return { ok: false, environment: "local-only" };
  const response = await fetch(`${apiBase}/api/health`);
  if (!response.ok) throw await responseError(response, "API health check failed");
  return response.json();
}

export async function getAssets(): Promise<AssetRecord[]> {
  const fallbackAssets = nonEmptyAssets(storage.get("cp-assets", defaultAssets));
  if (!apiBase) {
    return fallbackAssets;
  }
  try {
    const response = await fetch(`${apiBase}/api/assets`);
    if (!response.ok) throw new Error(`Assets failed: ${response.status}`);
    const payload = (await response.json()) as { assets: AssetRecord[] };
    return nonEmptyAssets(payload.assets);
  } catch {
    return [];
  }
}

export async function saveAsset(asset: AssetRecord): Promise<AssetRecord[]> {
  if (!apiBase) {
    const current = storage.get("cp-assets", await getAssets());
    const next = [asset, ...current.filter((item) => item.ticker !== asset.ticker)].filter((item) => item.active);
    storage.set("cp-assets", next);
    return next;
  }
  const response = await fetch(`${apiBase}/api/assets`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(asset)
  });
  if (!response.ok) throw new Error(`Asset save failed: ${response.status}`);
  const payload = (await response.json()) as { assets: AssetRecord[] };
  return payload.assets;
}

export async function deleteAsset(ticker: string): Promise<AssetRecord[]> {
  if (!apiBase) {
    const next = storage.get("cp-assets", await getAssets()).filter((item) => item.ticker !== ticker);
    storage.set("cp-assets", next);
    return next;
  }
  const response = await fetch(`${apiBase}/api/assets/${encodeURIComponent(ticker)}`, {
    method: "DELETE",
    headers: authHeaders()
  });
  if (!response.ok) throw new Error(`Asset delete failed: ${response.status}`);
  const payload = (await response.json()) as { assets: AssetRecord[] };
  return payload.assets;
}

type AlertPolicyWire = Partial<AlertPolicy> & {
  plan_id?: string;
  cooldown_minutes?: number;
  dedupe_scope?: AlertPolicy["dedupeScope"];
  market_hours_only?: boolean | number;
  require_ack?: boolean | number;
  notify_trigger?: boolean | number;
  notify_steps?: boolean | number;
  selected_step_ids?: string[] | string;
  repeat_mode?: AlertPolicy["repeatMode"];
  updated_at?: string;
};

function normalizeAlertPolicy(value: AlertPolicyWire): AlertPolicy {
  const selected = value.selectedStepIds ?? value.selected_step_ids ?? [];
  let selectedStepIds: string[] = [];
  if (Array.isArray(selected)) selectedStepIds = selected.filter((item): item is string => typeof item === "string");
  else {
    try {
      const parsed = JSON.parse(selected);
      if (Array.isArray(parsed)) selectedStepIds = parsed.filter((item): item is string => typeof item === "string");
    } catch {
      selectedStepIds = [];
    }
  }
  const bool = (camel: boolean | undefined, snake: boolean | number | undefined, fallback: boolean) =>
    camel ?? (snake === undefined ? fallback : Boolean(snake));
  return {
    id: value.id ?? crypto.randomUUID(),
    planId: value.planId ?? value.plan_id,
    name: value.name?.trim() || "Automatic ladder alert",
    severity: value.severity ?? "high",
    cooldownMinutes: Number(value.cooldownMinutes ?? value.cooldown_minutes ?? 30),
    dedupeScope: value.dedupeScope ?? value.dedupe_scope ?? "step",
    marketHoursOnly: bool(value.marketHoursOnly, value.market_hours_only, false),
    requireAck: bool(value.requireAck, value.require_ack, true),
    enabled: value.enabled !== false,
    notifyTrigger: bool(value.notifyTrigger, value.notify_trigger, true),
    notifySteps: bool(value.notifySteps, value.notify_steps, true),
    selectedStepIds,
    repeatMode: value.repeatMode ?? value.repeat_mode ?? "recross",
    template: value.template?.trim() || "[CP Dashboard] {{planName}} / {{stepLabel}} reached at {{price}} (planned {{level}}).",
    updatedAt: value.updatedAt ?? value.updated_at
  };
}

function normalizeAlertPolicies(values: AlertPolicyWire[]) {
  return values.map(normalizeAlertPolicy);
}

export async function getAlertPolicies(): Promise<AlertPolicy[]> {
  if (!apiBase) return storage.get("cp-alert-policies", [] as AlertPolicy[]);
  const response = await fetch(`${apiBase}/api/alert-policies`);
  if (!response.ok) throw new Error(`Alert policies failed: ${response.status}`);
  const payload = (await response.json()) as { policies: AlertPolicyWire[] };
  return normalizeAlertPolicies(payload.policies ?? []);
}

export async function saveAlertPolicy(policy: AlertPolicy): Promise<AlertPolicy[]> {
  if (!apiBase) {
    const current = storage.get("cp-alert-policies", [] as AlertPolicy[]);
    const next = [policy, ...current.filter((item) => item.id !== policy.id)];
    storage.set("cp-alert-policies", next);
    return next;
  }
  const response = await fetch(`${apiBase}/api/alert-policies`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders() },
    body: JSON.stringify(policy)
  });
  if (!response.ok) throw await responseError(response, "Alert policy save failed");
  const payload = (await response.json()) as { policies: AlertPolicyWire[] };
  return normalizeAlertPolicies(payload.policies ?? []);
}

export async function deleteAlertPolicy(policyId: string): Promise<AlertPolicy[]> {
  if (!apiBase) {
    const next = storage.get("cp-alert-policies", [] as AlertPolicy[]).filter((item) => item.id !== policyId);
    storage.set("cp-alert-policies", next);
    return next;
  }
  const response = await fetch(`${apiBase}/api/alert-policies/${encodeURIComponent(policyId)}`, {
    method: "DELETE",
    headers: authHeaders()
  });
  if (!response.ok) throw await responseError(response, "Alert policy delete failed");
  const payload = (await response.json()) as { policies: AlertPolicyWire[] };
  return normalizeAlertPolicies(payload.policies ?? []);
}

export async function getPlanVersions(planId: string) {
  if (!apiBase) return [];
  const response = await fetch(`${apiBase}/api/plans/${encodeURIComponent(planId)}/versions`);
  if (!response.ok) throw new Error(`Plan versions failed: ${response.status}`);
  const payload = (await response.json()) as { versions: unknown[] };
  return payload.versions;
}

export async function getExecutionAudit(planId?: string) {
  if (!apiBase) return [];
  const suffix = planId ? `?planId=${encodeURIComponent(planId)}` : "";
  const response = await fetch(`${apiBase}/api/execution-audit${suffix}`);
  if (!response.ok) throw new Error(`Execution audit failed: ${response.status}`);
  const payload = (await response.json()) as { audits: unknown[] };
  return payload.audits;
}

function chartFallback(instrumentId: string): ChartIntelligencePayload {
  const metric = monitoringMetricsMock.find((item) => item.instrument.id === instrumentId) ?? monitoringMetricsMock[0];
  return {
    instrument: metric.instrument,
    quote: {
      ...metric.quote,
      freshness: "offline",
      source: "Unavailable",
      staleReason: "Verified OHLC API is unavailable; synthetic candle fallback is disabled."
    },
    history: [],
    priorHigh: undefined,
    pairs: [],
    range: "10y"
  };
}
